import { Injectable, inject, signal, computed } from '@angular/core';
import { toSignal, toObservable }               from '@angular/core/rxjs-interop';
import { switchMap, filter, catchError }         from 'rxjs/operators';
import { of }                                    from 'rxjs';
import { Router }                                from '@angular/router';

import { HttpService }  from 'src/app/services/http.service';
import { TreeNode }     from '@libs/front/components/tree-view/tree-view.component';
import { BlastFlatDto } from 'src/app/shared/models/site-tree.dto';
import {
  TreeGroupingConfig,
  GroupingLevel,
  DEFAULT_TREE_GROUPING,
} from 'src/app/shared/models/tree-grouping.types';

// ── Types publics ─────────────────────────────────────────────────────────────

export type SiteNodeType =
  | 'project'
  | 'zone'
  | 'year'
  | 'month'
  | 'niveau'
  | 'status'
  | 'blast';

// ── Clés localStorage ─────────────────────────────────────────────────────────

const LS_SITE_ID       = 'siteNav.siteId';
const LS_PROJECT_ID    = 'siteNav.projectId';
const LS_SELECTED_NODE = 'siteNav.selectedNodeId';
const LS_GROUPING      = 'siteNav.grouping';

// ── Helpers localStorage ──────────────────────────────────────────────────────

function lsLoad(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function lsSave(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch {}
}

function lsClear(key: string): void {
  try { localStorage.removeItem(key); } catch {}
}

function lsLoadGrouping(): TreeGroupingConfig {
  try {
    const raw = localStorage.getItem(LS_GROUPING);
    if (raw) return { ...DEFAULT_TREE_GROUPING, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_TREE_GROUPING };
}

// ── Helpers date / grouping ───────────────────────────────────────────────────

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function getBestDate(blast: BlastFlatDto): Date | null {
  const d = blast.dateTirEffective ?? blast.dateTirPrevisionnelle;
  return d ? new Date(d) : null;
}

function getGroupValue(blast: BlastFlatDto, level: GroupingLevel): string {
  switch (level) {
    case 'project': return blast.nomProjet  || '—';
    case 'zone':    return blast.nomZone    || '—';
    case 'niveau':  return blast.niveau     || '—';
    case 'status':  return blast.estRealise ? 'Réalisé' : 'Planifié';
    case 'year': {
      const d = getBestDate(blast);
      return d ? String(d.getFullYear()) : '—';
    }
    case 'month': {
      const d = getBestDate(blast);
      return d ? MONTHS[d.getMonth()] : '—';
    }
    default: return '';
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class SiteNavigationService {

  private readonly http   = inject(HttpService);
  private readonly router = inject(Router);

  // ── Site courant ──────────────────────────────────────────────────────────
  // Initialisé à null — setSite() déclenche toujours le chargement HTTP.
  // Le localStorage ne sert qu'à mémoriser l'id, pas à déclencher le pipe.

  private readonly _siteId = signal<number | null>(null);

  readonly siteId = this._siteId.asReadonly();

  // ── Project courant ───────────────────────────────────────────────────────

  private readonly _projectId = signal<number | null>(
    Number(lsLoad(LS_PROJECT_ID)) || null
  );

  readonly projectId = this._projectId.asReadonly();

  // ── Nœud sélectionné ─────────────────────────────────────────────────────

  private readonly _selectedNodeId = signal<string>(
    lsLoad(LS_SELECTED_NODE) ?? ''
  );

  readonly selectedNodeId = this._selectedNodeId.asReadonly();

  // ── Config de grouping ────────────────────────────────────────────────────

  private readonly _grouping = signal<TreeGroupingConfig>(lsLoadGrouping());

  readonly grouping = this._grouping.asReadonly();

  // ── Chargement HTTP blasts ────────────────────────────────────────────────
  // GET Sites/{id}/blasts → BlastFlatDto[] (tableau brut)

  private readonly _data = toSignal(
    toObservable(this._siteId).pipe(
      filter((id): id is number => id !== null),
      switchMap(id =>
        this.http.get<BlastFlatDto[]>(`Sites/${id}/blasts`).pipe(
          catchError(() => of(null))
        )
      )
    ),
    { initialValue: null }
  );

  // ── Chargement HTTP site info ─────────────────────────────────────────────
  // GET Sites/{id} → { nom: string, ... }

  private readonly _site = toSignal(
    toObservable(this._siteId).pipe(
      filter((id): id is number => id !== null),
      switchMap(id =>
        this.http.get<{ nom: string }>(`Sites/${id}`).pipe(
          catchError(() => of(null))
        )
      )
    ),
    { initialValue: null }
  );

  // ── Computed publics ──────────────────────────────────────────────────────

  readonly siteName = computed(() => this._site()?.nom ?? '');

  readonly treeData = computed<TreeNode[]>(() => {
    const data     = this._data();
    const grouping = this._grouping();
    if (!data?.length) return [];
    return this._buildTree(data, grouping);
  });

  readonly selectedNodeId$ = computed<string>(() => this._selectedNodeId());

  // ── API publique ──────────────────────────────────────────────────────────

  /**
   * Appeler depuis SiteComponent (constructeur ou ngOnInit).
   * Toujours déclenche le chargement HTTP car _siteId part toujours de null.
   */
  setSite(siteId: number): void {
    lsSave(LS_SITE_ID, String(siteId));
    this._projectId.set(null);
    this._selectedNodeId.set('');
    lsClear(LS_PROJECT_ID);
    lsClear(LS_SELECTED_NODE);
    // Pas de garde — on set toujours pour déclencher le pipe
    this._siteId.set(siteId);
  }

  syncFromQueryParams(params: {
    projectId?: number | null;
    blastId?:   number | null;
    nodeType?:  string | null;
    nodeValue?: string | null;
  }): void {
    if (params.projectId) {
      this._projectId.set(params.projectId);
      lsSave(LS_PROJECT_ID, String(params.projectId));
    }

    let nodeId = '';
    if (params.blastId) {
      nodeId = `blast:${params.blastId}`;
    } else if (params.projectId && !params.nodeType) {
      nodeId = `project:${params.projectId}`;
    } else if (params.nodeType && params.nodeValue) {
      nodeId = `${params.nodeType}:${params.nodeValue}`;
    }

    this._selectedNodeId.set(nodeId);
    lsSave(LS_SELECTED_NODE, nodeId);
  }

  navigateFromNodeClick(event: { id: number | string; type: string }, tab?: string): void {
    const siteId = this._siteId();
    if (!siteId) return;

    const type = event.type as SiteNodeType;

    switch (type) {
      case 'project': {
        const rawId     = String(event.id);
        const nodeValue = rawId.includes(':')
          ? rawId.split(':').slice(1).join(':')
          : rawId;
        const projetId  = this._data()?.find(b => b.nomProjet === nodeValue)?.projetId ?? null;
        if (projetId) {
          this._projectId.set(projetId);
          lsSave(LS_PROJECT_ID, String(projetId));
        }
        this.router.navigate(['/new-site', siteId], {
          queryParams: { projectId: projetId, nodeType: 'project', nodeValue, tab },
        });
        break;
      }

      case 'blast': {
        const blast = this._data()?.find(b => b.tirId === Number(event.id));
        this.router.navigate(['/new-site', siteId], {
          queryParams: {
            projectId: this._projectId(),
            blastId:   event.id,
            nodeType:  'blast',
            nodeValue: blast?.nom ?? String(event.id),
            tab,
          },
        });
        break;
      }

      case 'zone':
      case 'year':
      case 'month':
      case 'niveau':
      case 'status': {
        const rawId     = String(event.id);
        const nodeValue = rawId.includes(':')
          ? rawId.split(':').slice(1).join(':')
          : rawId;
        this.router.navigate(['/new-site', siteId], {
          queryParams: { projectId: this._projectId(), nodeType: type, nodeValue, tab },
        });
        break;
      }
    }
  }

  setGrouping(config: TreeGroupingConfig): void {
    const safe: TreeGroupingConfig = {
      ...config,
      level3: config.level2 === 'none' ? 'none' : config.level3,
    };
    this._grouping.set(safe);
    lsSave(LS_GROUPING, JSON.stringify(safe));
  }

  reset(): void {
    this._siteId.set(null);
    this._projectId.set(null);
    this._selectedNodeId.set('');
    lsClear(LS_SITE_ID);
    lsClear(LS_PROJECT_ID);
    lsClear(LS_SELECTED_NODE);
  }

  // ── Builder dynamique ─────────────────────────────────────────────────────

  private _buildTree(blasts: BlastFlatDto[], config: TreeGroupingConfig): TreeNode[] {
    const levels: GroupingLevel[] = [config.level1];
    if (config.level2 !== 'none') levels.push(config.level2);
    if (config.level3 !== 'none') levels.push(config.level3);
    return this._groupRecursive(blasts, levels, 0);
  }

  private _groupRecursive(
    blasts: BlastFlatDto[],
    levels: GroupingLevel[],
    depth:  number,
  ): TreeNode[] {
    if (depth >= levels.length) return this._mapBlasts(blasts);

    const level  = levels[depth];
    const groups = new Map<string, BlastFlatDto[]>();

    for (const blast of blasts) {
      const key = getGroupValue(blast, level);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(blast);
    }

    const sortedKeys = [...groups.keys()].sort((a, b) =>
      (level === 'year' || level === 'month')
        ? Number(b) - Number(a) || b.localeCompare(a)
        : a.localeCompare(b)
    );

    return sortedKeys.map(key => ({
      id:       `${level}:${key}`,
      type:     level,
      label:    key,
      children: this._groupRecursive(groups.get(key)!, levels, depth + 1),
    }));
  }

  private _mapBlasts(blasts: BlastFlatDto[]): TreeNode[] {
    return blasts
      .sort((a, b) => a.nom.localeCompare(b.nom))
      .map(b => ({
        id:    b.tirId,
        type:  'blast',
        label: b.nom,
        badge: b.estRealise ? 'TREEVIEW.OPEN' : undefined,
      }));
  }
}
